// ── Combo chooser (shared between upload + avatars views) ────────────────────
// Renders the compatible-garment carousel: one horizontal-swipe section per
// compatible category, single-select tiles with thumbnail/name/price.

import { esc, singularize, toCanonicalCategory } from './helpers';
import type { ViewContext, ModalOpts, State } from './modal-types';

/** Human labels for canonical garment categories (try-on type names). */
const CATEGORY_LABELS: Record<string, string> = {
  top: 'Top',
  bottom: 'Bottom',
  outerwear: 'Outerwear',
  dress: 'Dress',
  hat: 'Hat',
  shoe: 'Shoe',
};

/** Friendly display label for a canonical category. */
export function comboCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/**
 * Resolve the canonical try-on type for the current product, in priority order:
 * state (background-resolved from tags) → opts.tryonType → opts.category →
 * opts.subcategory.
 */
export function resolveTryonType(
  opts: ModalOpts,
  state: Pick<State, 'tryonType'>,
): string | null {
  if (state.tryonType) return state.tryonType;
  if (opts.tryonType) return opts.tryonType;
  if (opts.category) {
    const c = toCanonicalCategory(opts.category);
    if (c) return c;
  }
  return opts.subcategory ? toCanonicalCategory(opts.subcategory) : null;
}

/** Combo CTA label: "Try Selected" once a product is chosen, else "Add Combo". */
export function comboCtaLabel(state: State): string {
  return state.comboOpen && state.selectedComboProduct ? 'Try Selected' : 'Add Combo';
}

/**
 * Primary submit label shared by the upload + avatar preview views.
 * Mirrors the garment subcategory (e.g. "Tops" → "Top") so the CTA reads
 * "Try this {Garment}" when known, else the generic "Try this on".
 */
export function tryOnSubmitLabel(
  opts: ModalOpts,
  state: Pick<State, 'resolvedCategory'>,
): string {
  const garmentCategory = singularize(
    opts.subcategory ?? state.resolvedCategory ?? opts.category ?? '',
  );
  return garmentCategory ? `Try this ${garmentCategory}` : 'Try this on';
}

const CHECK_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`;

/** Render the combo carousel panel. Returns '' when not open. */
export function comboChooserHTML(state: State): string {
  if (!state.comboOpen) return '';

  if (state.comboError) {
    return `<div class="ts-combo-error">${esc(state.comboError)}</div>`;
  }

  if (state.comboLoading) {
    return `
      <div class="ts-combo-loading">
        <div class="ts-spinner" style="width:22px;height:22px;margin:0 auto 8px"></div>
        <p style="margin:0;font-size:13px;color:#64748b">Finding compatible garments…</p>
      </div>
    `;
  }

  if (state.comboSections.length === 0) {
    return `<div class="ts-combo-error">No compatible garments found for this item.</div>`;
  }

  // One continuous horizontal row, grouped by category: each category's label
  // is followed by its tiles, so scrolling flows straight from one category
  // into the next.
  const tiles = state.comboSections
    .map(
      (s) => `
      <div class="ts-combo-section-title">${esc(comboCategoryLabel(s.category))}</div>
      ${s.products
        .map(
          (p, i) => `
          <button type="button" class="ts-combo-item ${state.selectedComboProduct?.id === p.id ? 'selected' : ''}"
            data-section="${esc(s.category)}" data-idx="${i}" data-id="${esc(String(p.id))}">
            <span class="ts-combo-thumb">
              ${p.image ? `<img src="${esc(p.image)}" alt="${esc(p.title)}" loading="lazy" />` : '<span class="ts-combo-noimg">—</span>'}
              <span class="ts-combo-check">${CHECK_SVG}</span>
            </span>
            <span class="ts-combo-name">${esc(p.title)}</span>
          </button>
        `,
        )
        .join('')}
    `,
    )
    .join('');

  return `<div class="ts-combo-carousel"><div class="ts-combo-track">${tiles}</div></div>`;
}

/** Wire the combo carousel tile selection handlers. */
export function wireComboChooser(ctx: ViewContext): void {
  ctx.content.querySelectorAll<HTMLButtonElement>('.ts-combo-item').forEach((tile) => {
    tile.addEventListener('click', () => {
      const section = ctx.state.comboSections.find(
        (s) => s.category === tile.dataset.section,
      );
      const product = section?.products[Number(tile.dataset.idx)];
      if (product) ctx.selectComboProduct(product);
    });
  });
}
